import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { chatService } from '../../services/chatService';
import ChatMessage from './ChatMessage';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  text: string;
  citations?: any[];
}

interface CitationRef {
  id: string;
  file_path?: string;
  page?: number[];
  chunk_index?: number;
  text_content?: string;
  fund?: string;
}

export interface ChatPanelRef {
  loadThread: (threadId: string, conversations: any[]) => void;
}

interface ChatPanelProps {
  onCitationClick: (citation: CitationRef, page: number) => void;
  onNewThread: () => void;
}

const ChatPanel = forwardRef<ChatPanelRef, ChatPanelProps>(({ onCitationClick, onNewThread }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    loadThread(newThreadId: string, conversations: any[]) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      setThreadId(newThreadId);
      if (!conversations || conversations.length === 0) {
        setMessages([]);
        return;
      }
      const msgs: Message[] = [];
      conversations.forEach((conv: any, i: number) => {
        msgs.push({ id: `q-${i}`, type: 'user', text: conv.question });
        if (conv.answer) {
          msgs.push({
            id: `a-${i}`,
            type: 'assistant',
            text: conv.answer,
            citations: conv.citations || [],
          });
        }
      });
      setMessages(msgs);
    },
  }));

  const pollForAnswer = useCallback(
    (tid: string, questionIndex: number) => {
      let attempts = 0;
      const maxAttempts = 120;

      pollingRef.current = setInterval(async () => {
        attempts++;
        if (attempts > maxAttempts) {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setLoading(false);
          return;
        }
        try {
          const res = await chatService.getThreadMessages(tid);
          const convs = res.data.conversations || [];
          if (convs.length > questionIndex && convs[questionIndex].answer) {
            if (pollingRef.current) clearInterval(pollingRef.current);

            const msgs: Message[] = [];
            convs.forEach((conv: any, i: number) => {
              msgs.push({ id: `q-${i}`, type: 'user', text: conv.question });
              if (conv.answer) {
                msgs.push({
                  id: `a-${i}`,
                  type: 'assistant',
                  text: conv.answer,
                  citations: conv.citations || [],
                });
              }
            });
            setMessages(msgs);
            setLoading(false);
          }
        } catch {
          // keep polling
        }
      }, 3000);
    },
    [],
  );

  const handleSend = async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;

    setInput('');
    const userMsg: Message = { id: `q-${Date.now()}`, type: 'user', text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      let currentThread = threadId;

      if (!currentThread) {
        const res = await chatService.sendMessage(text, '');
        currentThread = res.data.thread_id;
        setThreadId(currentThread);
        onNewThread();
        pollForAnswer(currentThread, 0);
      } else {
        await chatService.sendMessageToThread(text, currentThread);
        const res = await chatService.getThreadMessages(currentThread);
        const convCount = (res.data.conversations || []).length;
        pollForAnswer(currentThread, convCount - 1);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setLoading(false);
      setMessages((prev) => [
        ...prev,
        { id: `err-${Date.now()}`, type: 'assistant', text: 'Failed to get a response. Please try again.' },
      ]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSendClick = () => {
    handleSend();
  };

  const suggestedQuestions = [
    'What are the fund strategy, sector focus, and geography constraints?',
    'Summarize the latest portfolio performance and key attribution drivers.',
    'How does the hedge fund manage gross/net exposure and downside risk?',
    'What are the PE fund investment period, target IRR, and exit assumptions?',
  ];

  const showCenteredComposer = messages.length === 0 && !loading;

  const renderComposer = () => (
    <div className="max-w-3xl mx-auto w-full">
      <div className="flex items-end gap-2 bg-white border border-gray-300 rounded-2xl px-3 py-2 shadow-sm focus-within:border-gray-500 focus-within:ring-1 focus-within:ring-gray-400">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a question..."
          rows={1}
          className="flex-1 bg-transparent resize-none border-none outline-none text-sm text-gray-800 placeholder-gray-400 max-h-32"
          disabled={loading}
        />
        <button
          onClick={handleSendClick}
          disabled={loading || !input.trim()}
          className="p-2 bg-gray-900 text-white rounded-full hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
        {showCenteredComposer && (
          <div className="h-full flex flex-col items-center justify-center">
            <div className="text-center max-w-md mb-6">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">DDQ Query Engine</h2>
              <p className="text-gray-500 text-sm">Ask anything about the knowledge base.</p>
            </div>
            <div className="w-full max-w-3xl mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {suggestedQuestions.map((question) => (
                  <button
                    key={question}
                    onClick={() => handleSend(question)}
                    className="text-left text-xs px-3 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>
            {renderComposer()}
          </div>
        )}

        <div className={`max-w-3xl mx-auto space-y-4 ${showCenteredComposer ? 'hidden' : ''}`}>
          {messages.map((msg) => (
            <ChatMessage
              key={msg.id}
              type={msg.type}
              text={msg.text}
              citations={msg.citations}
              onCitationClick={onCitationClick}
            />
          ))}

          {loading && (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-600 text-xs font-bold">AI</span>
              </div>
              <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      {!showCenteredComposer && <div className="border-t border-gray-200 bg-white px-4 py-4">{renderComposer()}</div>}
    </div>
  );
});

ChatPanel.displayName = 'ChatPanel';

export default ChatPanel;
